import { Avatar, AvatarFallback, AvatarImage } from '@talentmesh/ui/components/avatar';
import { Badge } from '@talentmesh/ui/components/badge';
import { Button } from '@talentmesh/ui/components/button';
import { Card } from '@talentmesh/ui/components/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@talentmesh/ui/components/dropdown-menu';
import { MoreHorizontal, ArrowRight } from 'lucide-react';
import { generatePath, Link } from 'react-router';
import { resourceBaseUrl } from '@/AppSettings';
import { ClientOverviewResponse } from '@/DTO/Clients/ClientOverviewResponse';
import useCompanyId from '@/Hooks/UseCompanyId';
import RoutePath from '@/Routing/RoutePath';
import UIStrings from '@/Utils/UIStrings';

interface ClientCardShadcnProps extends ClientOverviewResponse {
    onEditClient: (clientToEdit: ClientOverviewResponse) => void;
}

const formatUrl = (url: string | null): string | null => {
    if (!url) return null;
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

const ClientCard = ({ onEditClient, ...client }: ClientCardShadcnProps) => {
    const companyId = useCompanyId();
    const clientId = client.id;

    const assessmentPath = generatePath(RoutePath.AssessmentsActive, { companyId, clientId });

    return (
        <Link
            to={assessmentPath}
            className="focus-visible:ring-ring rounded-2xl transition-all duration-200 ease-out outline-none hover:-translate-y-1 hover:shadow-md focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-[0.99] active:shadow"
        >
            <Card data-cy="ClientCard" className="flex h-full flex-col overflow-hidden">
                <div className="flex flex-1 flex-col p-5">
                    <div data-cy="clientCardHeader" className="flex items-start gap-4">
                        <Avatar data-cy="ClientIcon" className="size-11 shrink-0 rounded-full">
                            <AvatarImage
                                src={client.iconURL ?? undefined}
                                alt={UIStrings.CompanyNameLogo(client.name)}
                            />
                            <AvatarFallback className="bg-primary-50 text-primary text-xs font-semibold">
                                <img
                                    src={`${resourceBaseUrl}/clients/client_placeholder.svg`}
                                    alt="client placeholder"
                                />
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                            <span className="text-foreground group-hover:text-primary truncate text-xl font-semibold transition-[color,font-size] duration-200 ease-out group-hover:font-bold">
                                {client.name}
                            </span>
                            {client.isDefault && (
                                <Badge variant="secondary" className="shrink-0 text-[11px]">
                                    {UIStrings.MyCompany}
                                </Badge>
                            )}
                            {client.websiteURL && (
                                <span className="text-muted-foreground truncate text-sm">
                                    {formatUrl(client.websiteURL)}
                                </span>
                            )}
                        </div>
                        {!client.isDefault && (
                            <div data-cy="ClientCardActions" className="shrink-0">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="size-8 shrink-0 rounded-lg"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                        >
                                            <MoreHorizontal className="text-muted-foreground size-4" />
                                            <span className="sr-only">Open menu</span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                        <DropdownMenuItem onSelect={() => onEditClient(client)}>
                                            {UIStrings.EditCompany}
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        )}
                    </div>

                    <div className="bg-primary-foreground mt-5 flex items-center justify-between rounded-xl px-4 py-3">
                        <div>
                            <p className="text-muted-foreground mb-1 text-xs font-semibold tracking-wider uppercase">
                                {UIStrings.Active}
                            </p>
                            <p className="text-foreground text-2xl font-bold">{client.activeRecruitmentCount}</p>
                        </div>
                        <div className="bg-card text-primary flex size-8 items-center justify-center rounded-lg shadow transition-transform duration-150 group-hover:translate-x-0.5">
                            <ArrowRight className="size-4" />
                        </div>
                    </div>
                </div>
            </Card>
        </Link>
    );
};

export default ClientCard;
